import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { AnimateModule, HeaderComponent, NewsletterComponent, TrainingBrand } from '@pro-connect/shared';
import { BasicPage, WwwRouteSlug } from '../../../../models';
import { BrandSection } from './models/brand-section';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Navigate } from '@ngxs/router-plugin';
import { BrandTopComponent } from './components/brand-top/brand-top.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BrandDedicatedTrainingsComponent } from './components/brand-dedicated-trainings/brand-dedicated-trainings.component';
import { TrainingsListComponent } from '../../components/trainings-list/trainings-list.component';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { TrainingsListStateNullifyInit } from '../../components/trainings-list/trainings-list.state';

@Component({
  templateUrl: './brand.component.html',
  styleUrls: ['./brand.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    AnimateModule,
    HeaderComponent,
    NewsletterComponent,
    BrandTopComponent,
    BrandDedicatedTrainingsComponent,
    TrainingsListComponent,
  ],
})
export class BrandComponent extends BasicPage {
  public override loading: Signal<boolean> = signal(false);

  @ViewChild('trainingsList', {
    read: ElementRef,
  })
  public trainingsList: ElementRef | null = null;

  protected readonly AboutUsSection = BrandSection;
  protected readonly WwwRouteSlug = WwwRouteSlug;

  public brand: TrainingBrand;
  private readonly _angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);
  public reloadTrainignsData: WritableSignal<boolean> = signal(false);

  constructor(
    public readonly _activateRoute: ActivatedRoute,
    public readonly _router: Router,
  ) {
    super();
    this.brand = this._activateRoute.snapshot.params['brand'];
    if (!Object.values(TrainingBrand).includes(this.brand)) {
      console.error('Brand not found');
      this._store.dispatch(new Navigate([`/${WwwRouteSlug.NOT_FOUND_PAGE}`]));
    }

    _router.events.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.reloadTrainignsData.set(true);
        setTimeout(() => {
          this.reloadTrainignsData.set(false);
        });
        this.brand = this._activateRoute.snapshot.params['brand'];

        if (!Object.values(TrainingBrand).includes(this.brand)) {
          console.error('Brand not found');
          this._store.dispatch(new Navigate([`/${WwwRouteSlug.NOT_FOUND_PAGE}`]));
        }
      }
    });
  }

  public ngOnInit(): void {
    this._pushLayer();
  }

  get color(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return 'rgba(0, 0, 0, 0.87)';
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return '#fff';
      case TrainingBrand.REDKEN:
        return '#000';
      default:
        return '#000';
    }
  }

  get backgroundColor(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return '#FFF07F';
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return '#F2F2F2';
      case TrainingBrand.REDKEN:
        return '#F27F80';
      default:
        return '#fff';
    }
  }

  public get brandImage(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return 'assets/images/pages/brand/brand-banner-kerastase-full.webp';
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return 'assets/images/pages/brand/brand-banner-loreal-full.webp';
      case TrainingBrand.REDKEN:
        return 'assets/images/pages/brand/brand-banner-redken.webp';
      default:
        return '';
    }
  }

  public get brandLogo(): string {
    if (isPlatformBrowser(this._platformId)) {
      const isDesktopVersion = window.innerWidth > 991;
      switch (this.brand) {
        case TrainingBrand.KERASTASE:
          return 'assets/images/kerastase.svg';
        case TrainingBrand.LOREAL_PROFESSIONNEL:
          return isDesktopVersion ? 'assets/images/lp-white.png' : 'assets/images/loreal.png';
        case TrainingBrand.REDKEN:
          return 'assets/images/redken.svg';
        default:
          return '';
      }
    }

    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return 'assets/images/kerastase.svg';
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return 'assets/images/lp-white.png';
      case TrainingBrand.REDKEN:
        return 'assets/images/redken.svg';
      default:
        return '';
    }
  }

  public get brandLogoMobile(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return 'assets/images/kerastase.svg';
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return 'assets/images/loreal.png';
      case TrainingBrand.REDKEN:
        return 'assets/images/redken.svg';
      default:
        return '';
    }
  }

  public get brandTitle(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return '';
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return '';
      case TrainingBrand.REDKEN:
        return '';
      default:
        return '';
    }
  }

  public get brandDescription(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return `
          Luksusowa marka znana ze swojego innowacyjnego podejścia do piękna i zdrowia włosów. Od ponad 60 lat buduje swoją reputację, oferując wyjątkową, luksusową pielęgnację - wspieraną nauką w połączeniu z profesjonalnym doświadczeniem najlepszych fryzjerów na świecie. Kérastase zapewnia wszystkim wymagającym włosom wysokiej jakości, spersonalizowane, luksusowe rozwiązania pielęgnacyjne.
        `;
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return `
         Wiodąca na świecie, ikoniczna marka dla stylistów fryzur współpracująca z ponad 1,5 mln fryzjerów na całym świecie. Oferuje profesjonalną edukację w koloryzacji, pielęgnacji i stylizacji, współpracując z uznanymi światowymi ekspertami. Wspiera fryzjerów w budowaniu pewności siebie i doskonaleniu rzemiosła, umożliwiając osiąganie mistrzowskiego poziomu.
        `;
      case TrainingBrand.REDKEN:
        return `
          Łączy nowatorskie technologie z praktycznymi warsztatami, wspierając fryzjerów w rozwijaniu umiejętności technicznych i kreatywności w koloryzacji, stylizacji i pielęgnacji włosów.
          Marka współpracuje z uznanymi ekspertami i edukatorami, inspirując do samorozwoju oraz tworzenia innowacyjnych stylizacji, co zapewnia profesjonalistom lepsze zrozumienie produktów i technik pracy.
        `;
      default:
        return '';
    }
  }

  public get title(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return `LOREM IPSUM DOLOR SIT AMET`;
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return `LOREM IPSUM DOLOR SIT AMET`;
      case TrainingBrand.REDKEN:
        return `LOREM IPSUM DOLOR SIT AMET`;
      default:
        return '';
    }
  }

  public get description(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return `
          Lorem ipsum dolor sit amet, consectetur adipiscing elit.
          Vivamus vehicula, nibh ac iaculis vehicula, nunc sem sagittis dolor, ac condimentum turpis nunc non purus.
          Etiam egestas purus non sem facilisis finibus. Nam vitae turpis felis. Nunc congue tristique quam, vel venenatis elit semper id.
          Suspendisse viverra sapien at elit porta, in fringilla dolor blandit.
          Sed sollicitudin, diam a sodales maximus, mauris justo pretium justo, quis vestibulum lectus justo quis massa.
          Integer facilisis arcu vel hendrerit hendrerit.
          Proin vulputate, risus sed imperdiet cursus, est quam lobortis nibh, sit amet venenatis eros neque sit amet turpis. Nunc vitae gravida sapien.
          Pellentesque accumsan dignissim scelerisque.
          Fusce gravida odio nec nunc malesuada, id interdum nulla viverra.
        `;
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return `
          Lorem ipsum dolor sit amet, consectetur adipiscing elit.
          Vivamus vehicula, nibh ac iaculis vehicula, nunc sem sagittis dolor, ac condimentum turpis nunc non purus.
          Etiam egestas purus non sem facilisis finibus. Nam vitae turpis felis. Nunc congue tristique quam, vel venenatis elit semper id.
          Suspendisse viverra sapien at elit porta, in fringilla dolor blandit.
          Sed sollicitudin, diam a sodales maximus, mauris justo pretium justo, quis vestibulum lectus justo quis massa.
          Integer facilisis arcu vel hendrerit hendrerit.
          Proin vulputate, risus sed imperdiet cursus, est quam lobortis nibh, sit amet venenatis eros neque sit amet turpis. Nunc vitae gravida sapien.
          Pellentesque accumsan dignissim scelerisque.
          Fusce gravida odio nec nunc malesuada, id interdum nulla viverra.
        `;
      case TrainingBrand.REDKEN:
        return `
          Lorem ipsum dolor sit amet, consectetur adipiscing elit.
          Vivamus vehicula, nibh ac iaculis vehicula, nunc sem sagittis dolor, ac condimentum turpis nunc non purus.
          Etiam egestas purus non sem facilisis finibus. Nam vitae turpis felis. Nunc congue tristique quam, vel venenatis elit semper id.
          Suspendisse viverra sapien at elit porta, in fringilla dolor blandit.
          Sed sollicitudin, diam a sodales maximus, mauris justo pretium justo, quis vestibulum lectus justo quis massa.
          Integer facilisis arcu vel hendrerit hendrerit.
          Proin vulputate, risus sed imperdiet cursus, est quam lobortis nibh, sit amet venenatis eros neque sit amet turpis. Nunc vitae gravida sapien.
          Pellentesque accumsan dignissim scelerisque.
          Fusce gravida odio nec nunc malesuada, id interdum nulla viverra.
        `;
      default:
        return '';
    }
  }

  public get bannerClassName(): string {
    switch (this.brand) {
      case TrainingBrand.KERASTASE:
        return `kerastase`;
      case TrainingBrand.LOREAL_PROFESSIONNEL:
        return `loreal`;
      case TrainingBrand.REDKEN:
        return `redken`;
      default:
        return '';
    }
  }

  private _pushLayer(): void {
    this._angulartics2GoogleTagManager.pushLayer({
      brand: 'LDB',
      language: 'pl',
      country: 'Pl',
      siteTypeLevel: this._isDevelopment ? 'test' : 'main',
      pageCategory: 'product selector page',
      geoIPLocation: 'Pl',
      uiLoggedStatus: !!this.user() ? 'logged' : 'not logged',
      uiType: this.user()?.type,
    });
  }
}
